/**
 * The layer tree: the serialisable description of one composited frame.
 *
 * The host owns this state, the Layout Page renders it, and it crosses a
 * WebSocket between them - so everything here must survive `JSON.stringify`.
 * No class instances, no DOM references, no track objects: a video layer names
 * the track it wants and the renderer resolves it locally.
 */

export const LAYER_TYPES = [
  'video',
  'auto',
  'screenshare',
  'image',
  'text',
  'shape',
  'template',
] as const;

export type LayerType = (typeof LAYER_TYPES)[number];

/** The composited frame is always reasoned about at this logical size. */
export const CANVAS_WIDTH = 1920;
export const CANVAS_HEIGHT = 1080;

/**
 * The rate the composition is drawn and recorded at.
 *
 * One constant for both, because they must not disagree: drawing faster than
 * the recording is work thrown away, and on the media server it is thrown away
 * on the same three cores that are encoding - which is exactly where a
 * composition that judders in the file was losing its frames. Drawing slower
 * would judder for the opposite reason.
 *
 * The width and height already learned this lesson the expensive way, when a
 * 1920x1080 stage was recorded at 1280x720 and came out cropped.
 */
export const CANVAS_FPS = 30;

export const SHAPE_KINDS = ['rect', 'circle', 'ellipse'] as const;
export type ShapeKind = (typeof SHAPE_KINDS)[number];

/** How a video or image fills a box whose aspect ratio differs from its own. */
export const FIT_MODES = ['cover', 'contain', 'fill'] as const;
export type FitMode = (typeof FIT_MODES)[number];

export const TEXT_ALIGNMENTS = ['left', 'center', 'right'] as const;
export type TextAlignment = (typeof TEXT_ALIGNMENTS)[number];

/** Fraction of the source to keep, 0-1, before it is fitted into the box. */
export interface Crop {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Properties every layer carries, whatever it draws. */
export interface LayerBase {
  id: string;
  name: string;
  /** Pixels, in the 1920x1080 logical space - never in screen pixels. */
  x: number;
  y: number;
  width: number;
  height: number;
  /** Degrees, clockwise, about the layer's centre. */
  rotation: number;
  /** 0-1. */
  opacity: number;
  visible: boolean;
  /** A locked layer still renders; it just cannot be edited. */
  locked: boolean;
  zIndex: number;
}

/**
 * A participant's camera, or one of the host's extra multicam sources.
 *
 * `trackSid` identifies a specific publication and is the precise choice, but
 * it changes whenever the track is republished. `participantIdentity` plus
 * `trackName` survives that, which is why both are here: the renderer prefers
 * the sid and falls back to the pair.
 */
export interface VideoLayer extends LayerBase {
  type: 'video';
  trackSid: string | null;
  participantIdentity: string | null;
  /** `cam-1`, `mic-1`, or empty for a participant's primary camera. */
  trackName: string | null;
  fit: FitMode;
  crop: Crop | null;
  /** Mirrors the image horizontally, as a confidence monitor would. */
  mirrored: boolean;
}

export const AUTO_ARRANGEMENTS = ['grid'] as const;
export type AutoArrangement = (typeof AUTO_ARRANGEMENTS)[number];

/**
 * Every active participant, arranged inside one box.
 *
 * Unlike every other layer this one names nobody: it is resolved at draw time
 * from whoever is in the room. That makes it the only way the programme can
 * change without a transition - somebody joining appears immediately - which
 * is the point of it, and the one exception to the frozen programme snapshot.
 *
 * **The order must not depend on the renderer.** The studio, each guest and
 * Egress resolve this layer separately; if they sorted by arrival they would
 * disagree, and the director would be framing a picture the audience is not
 * seeing. Renderers sort by participant identity, which every one of them
 * agrees on.
 */
export interface AutoLayer extends LayerBase {
  type: 'auto';
  arrangement: AutoArrangement;
  /** Pixels between tiles, in the 1920x1080 logical space. */
  gap: number;
  fit: FitMode;
  /** Rounded corners on each tile. */
  cornerRadius: number;
}

export interface ScreenShareLayer extends LayerBase {
  type: 'screenshare';
  trackSid: string | null;
  participantIdentity: string | null;
  fit: FitMode;
  /**
   * Cropping a shared desktop is the common case, not the exotic one: a taskbar
   * or a second monitor is nearly always in shot and nearly never wanted.
   */
  crop: Crop | null;
}

export interface ImageLayer extends LayerBase {
  type: 'image';
  url: string;
  fit: FitMode;
}

export interface TextLayer extends LayerBase {
  type: 'text';
  text: string;
  fontFamily: string;
  fontSize: number;
  fontWeight: number;
  color: string;
  align: TextAlignment;
  /** Multiplier of the font size. */
  lineHeight: number;
  strokeColor: string | null;
  strokeWidth: number;
}

export interface ShapeLayer extends LayerBase {
  type: 'shape';
  shape: ShapeKind;
  fill: string | null;
  strokeColor: string | null;
  strokeWidth: number;
  /** Rounded corners, `rect` only. */
  cornerRadius: number;
}

/**
 * Declared now so the layer tree does not change shape in Phase 7, but not
 * rendered: `phase-03.md` says to stub the type. The renderer draws a visible
 * placeholder rather than nothing, so a host who adds one is not left
 * wondering whether it failed.
 */
export interface TemplateLayer extends LayerBase {
  type: 'template';
  templateId: string | null;
  values: Record<string, string>;
}

export type Layer =
  VideoLayer | AutoLayer | ScreenShareLayer | ImageLayer | TextLayer | ShapeLayer | TemplateLayer;

/** The whole composition, in the order the host arranged it. */
export type LayerTree = Layer[];

/** Sorts by zIndex without mutating the caller's array. */
export function sortedByZIndex(layers: LayerTree): LayerTree {
  return [...layers].sort((a, b) => a.zIndex - b.zIndex);
}
